// Remote FT3D precast wind-up. The receiving scene requires both caster and target to be locally perceived.

import type { WSMessage } from './types';
import { isOwnTargetingPreview, targetingPreviewStreamKey } from '../shared/ft3d-targeting-preview-client';
import { createPreviewPostQueue } from './ft3d-ability-targeting-preview-post';
import { ws } from './state';

type PreviewMessage = Extract<WSMessage, { type: 'ft3d_ability_targeting_preview' }>;
type ClearMessage = Extract<WSMessage, { type: 'ft3d_ability_targeting_preview_clear' }>;
const latestSequenceByUser = new Map<string, number>();

function isV1(data: PreviewMessage | ClearMessage): boolean {
    return data.schema === 'ft.ability-targeting.remote-preview.v1' && Number(data.version) === 1;
}

function isOwnMessage(data: PreviewMessage | ClearMessage): boolean {
    const context = getSidelogContext();
    return Boolean(context && isOwnTargetingPreview(data, context.user_id));
}

function acceptsSequence(data: PreviewMessage | ClearMessage): boolean {
    const userId = targetingPreviewStreamKey(data);
    const sequence = Number(data.sequence);
    if (!data.from_user_id || !Number.isSafeInteger(sequence) || sequence < 1) return false;
    if (sequence <= (latestSequenceByUser.get(userId) ?? 0)) return false;
    latestSequenceByUser.set(userId, sequence);
    return true;
}

function handleFt3dAbilityTargetingPreview(data: PreviewMessage): void {
    if (!isV1(data) || !checkIfValidUUID(data.encounter_id) || !checkIfValidUUID(data.caster_id)
        || (data.target_id && !checkIfValidUUID(data.target_id))
        || !acceptsSequence(data)) return;
    if (isOwnMessage(data)) return;
    window.dispatchEvent(new CustomEvent('ft:remote-ability-targeting-preview', { detail: data }));
}

function handleFt3dAbilityTargetingPreviewClear(data: ClearMessage): void {
    if (!isV1(data) || !checkIfValidUUID(data.encounter_id) || !acceptsSequence(data)
        || isOwnMessage(data)) return;
    window.dispatchEvent(new CustomEvent('ft:remote-ability-targeting-preview-clear', { detail: data }));
}

window.handleFt3dAbilityTargetingPreview = handleFt3dAbilityTargetingPreview;
window.handleFt3dAbilityTargetingPreviewClear = handleFt3dAbilityTargetingPreviewClear;

const previewSockets = new WeakSet<WebSocket>();
export function consumeTargetingPreviewTransportControl(socket: WebSocket,
    control: { type?: string; targetingPreview?: boolean; reason?: string }): boolean {
    if (control.type === 'transport_ready') {
        if (control.targetingPreview === true) previewSockets.add(socket);
        return false;
    }
    if (control.type !== 'transport_targeting_preview') return false;
    ftDebugLog('ws', { targetingPreviewRefused: control.reason ?? 'targeting-preview:transport-unavailable' });
    return true;
}

// Keep spacing, cast holds and newest-wins coalescing in front of both transports.
type PreviewQueueHost = { __ft3dAbilityTargetingPreviewQueue?: ReturnType<typeof createPreviewPostQueue> };
const previewPosts = (window as PreviewQueueHost).__ft3dAbilityTargetingPreviewQueue ??= createPreviewPostQueue(body => {
    if (ws && ws.readyState === WebSocket.OPEN && previewSockets.has(ws)) {
        const payload = JSON.parse(body) as Record<string, unknown>;
        const clearing = payload.action === 'clear';
        if (ws.bufferedAmount <= 16_384 || clearing) ws.send(JSON.stringify({ ...payload,
            type: clearing ? 'ft3d_ability_targeting_preview_clear' : 'ft3d_ability_targeting_preview',
            // The legacy page serializes this PHP boolean as 1/0.
            uses_reach: payload.uses_reach === true || payload.uses_reach === 1 }));
        return Promise.resolve();
    }
    ftDebugLog('ws', { targetingPreviewRefused: 'targeting-preview:transport-unavailable' });
    return Promise.resolve();
});
Object.assign(window, { ft3dQueueAbilityTargetingPreview: previewPosts.post, ft3dAbilityTargetingPreviewPosts: previewPosts.stats });

export { handleFt3dAbilityTargetingPreview, handleFt3dAbilityTargetingPreviewClear };
